import USStates from '@deltek/specification-client/core/Constants/usStates'
import type { AssociationModifierList } from '@deltek/specification-client/core/Models/associationModifierList'
import { availableProjectParamters } from 'shared/utils/projectParameterUtils'
import { BP_MODE } from '../constants'
import {
    BestPracticeTip,
    BPStagedConfig,
    emptyParameterSelected,
    emptyParameterToggles,
    parameterConfig,
    ParameterKey,
    ParameterOptionsMap,
    ParameterSelectedState,
    ParameterToggleState,
} from '../types/bpManagement.types'

export const labelToParameterKey: Record<string, ParameterKey> = {
    Owner: 'owner',
    'Building Type': 'buildingType',
    'Construction Type': 'constructionType',
    'Contract Type': 'contractType',
    Sustainability: 'sustainability',
    'Geographic Region': 'geographicRegion',
    'Seismic Zone': 'seismicZone',
    'Wind Zone': 'windZone',
}

interface FirmAssociationModifier {
    id?: string
    name?: string
}

interface FirmProjectGroup {
    name?: string
}

export interface FirmModifierLookupEntry {
    modifierListId: string
    modifiers: Record<string, string>
}
export type FirmModifierLookup = Partial<
    Record<ParameterKey, FirmModifierLookupEntry>
>

export interface FirmDataShape {
    firm?: {
        projectGroups?: FirmProjectGroup[]
        associationModifierLists?: Partial<AssociationModifierList>[]
    }
}

const buildEmptyOptionsMap = (): ParameterOptionsMap => ({
    ...emptyParameterSelected,
    provinceState: Object.values(USStates),
})

export interface MappedBPParameterState {
    isGlobal: boolean
    parameterToggles: ParameterToggleState
    parameterSelected: ParameterSelectedState
}

/** Maps a BestPracticeTip (from audit history) to parameter panel state. */
export const mapTipToParameterState = (
    tip: BestPracticeTip
): MappedBPParameterState => {
    const parameterToggles: ParameterToggleState = { ...emptyParameterToggles }
    const parameterSelected: ParameterSelectedState = { ...emptyParameterSelected }

    if (tip.type === 'global') {
        return { isGlobal: true, parameterToggles, parameterSelected }
    }

    Object.entries(tip.parameters ?? {}).forEach(([key, value]) => {
        if (!value) return
        parameterToggles[key as ParameterKey] = true
        parameterSelected[key as ParameterKey] = [value]
    })

    return { isGlobal: false, parameterToggles, parameterSelected }
}

/**
 * Converts a tip's config into the equivalent BPStagedConfig snapshot.
 * Used to detect whether the user changed anything before calling the update mutation.
 */
export const tipToStagedConfig = (tip: BestPracticeTip): BPStagedConfig => {
    if (tip.type === 'global') {
        return { mode: BP_MODE.GLOBAL, parameterSelected: { ...emptyParameterSelected } }
    }
    const params = tip.parameters ?? {}
    const parameterSelected: ParameterSelectedState = { ...emptyParameterSelected }
    ;(Object.entries(params) as [ParameterKey, string | undefined][]).forEach(([key, value]) => {
        if (value) parameterSelected[key] = [value]
    })
    const projectGroup = params.projectGroup ?? undefined
    const city = parameterSelected.city[0] ?? undefined
    const provinceState = parameterSelected.provinceState[0] ?? undefined
    const hasSelection =
        !!projectGroup ||
        parameterConfig.some(
            ({ key }) => key !== 'projectGroup' && (parameterSelected[key]?.length ?? 0) > 0
        )
    return {
        mode: hasSelection ? BP_MODE.PARAMETER : BP_MODE.NONE,
        projectGroup,
        city,
        provinceState,
        parameterSelected,
    }
}

/**
 * Walk firmData once and produce both shapes the BP feature needs:
 *  - `options`: ParameterKey → string[] (names) for the panel dropdowns
 *  - `lookup`: ParameterKey → modifier-list id + name→id map for mutation input
 */
export const buildFirmBPParameters = (
    firmData: FirmDataShape | null | undefined
): { options: ParameterOptionsMap; lookup: FirmModifierLookup } => {
    const options = buildEmptyOptionsMap()
    const lookup: FirmModifierLookup = {}

    const groups = firmData?.firm?.projectGroups
    if (groups) {
        options.projectGroup = groups
            .map((g) => g.name)
            .filter((n): n is string => Boolean(n))
    }

    const lists = firmData?.firm?.associationModifierLists
    if (lists) {
        lists.forEach((modifierList) => {
            const paramConfig = availableProjectParamters.find(
                (p) => p.name === modifierList.name
            )
            if (!paramConfig) return
            const paramKey = labelToParameterKey[paramConfig.label]
            if (!paramKey) return

            const modifiers: Record<string, string> = {}
            const names: string[] = []
            ;(modifierList.associationModifiers || []).forEach(
                (mod: FirmAssociationModifier) => {
                    if (!mod.name) return
                    names.push(mod.name)
                    if (mod.id) modifiers[mod.name] = mod.id
                }
            )

            options[paramKey] = names
            lookup[paramKey] = {
                modifierListId: modifierList.id || '',
                modifiers,
            }
        })
    }

    return { options, lookup }
}
